/**
 * 2026-10-08 · a master record's history, start to finish: created → processes added → sent for
 * approval → approved / not approved (and re-sent, and approved again) → finalised → the record's
 * approval chain. Only what happened is drawn — a step not reached is simply not on the line.
 * Everything comes from the server (`/designer/recipes/:id/history/`); the drawer only arranges
 * it: a milestone strip, one group per day, and processes added in one act folded into one row.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Empty, Segmented, Select, Skeleton, Tag, Tooltip } from "antd";
import { CheckCircle2, CircleDot, FilePlus2, Flag, History, Layers, RotateCcw, Send, XCircle } from "lucide-react";
import { getRecordHistory } from "./designerApi";
import { formatDate, formatTime } from "@/utils/datetime";
import { useTheme } from "@/App";


// One look per tone the server sends.
const TONE = {
  created:  { color: "#1677ff", bg: "rgba(22,119,255,0.10)",  Icon: FilePlus2,    label: "Created" },
  added:    { color: "#64748b", bg: "rgba(100,116,139,0.12)", Icon: Layers,       label: "Process" },
  sent:     { color: "#d48806", bg: "rgba(250,173,20,0.14)",  Icon: Send,         label: "Sent" },
  approved: { color: "#389e0d", bg: "rgba(82,196,26,0.13)",   Icon: CheckCircle2, label: "Approved" },
  rejected: { color: "#cf1322", bg: "rgba(255,77,79,0.12)",   Icon: XCircle,      label: "Not approved" },
  returned: { color: "#d46b08", bg: "rgba(250,140,22,0.13)",  Icon: RotateCcw,    label: "Returned" },
  final:    { color: "#531dab", bg: "rgba(114,46,209,0.12)",  Icon: Flag,         label: "Final" },
  neutral:  { color: "#8c8c8c", bg: "rgba(140,140,140,0.12)", Icon: CircleDot,    label: "" },
};

const dayOf = (iso) => { try { return formatDate(iso); } catch (e) { return String(iso).slice(0, 10); } };
const timeOf = (iso) => { try { return formatTime(iso); } catch (e) { return String(iso).slice(11, 16); } };

/** Processes added by one person in the same minute are one act — "7 processes added". */
function foldAdded(events) {
  const out = [];
  events.forEach((e) => {
    const prev = out[out.length - 1];
    if (e.tone === "added" && prev && prev.tone === "added" && prev.actor === e.actor
        && String(prev.at).slice(0, 16) === String(e.at).slice(0, 16)) {
      prev.processes.push(e.process);
      return;
    }
    out.push(e.tone === "added" ? { ...e, processes: [e.process] } : e);
  });
  return out.map((e) => (e.tone === "added" && e.processes.length > 1
    ? { ...e, title: `${e.processes.length} processes added`, process: null } : e));
}

/**
 * Where the record is, stage by stage, up to Finalise. The two ends are fixed — created and
 * finalised — and between them one stage per PROCESS, as many as the record has,
 * each with its own approval state. Exactly one stage is "current": the first that is in review,
 * not approved, or waiting to be acted on.
 */
function stages(data) {
  const all = data?.events || [];
  const processes = data?.recipe?.processes || [];
  const by = (e) => [e.actor, e.role ? `(${e.role})` : ""].filter(Boolean).join(" ");
  const created = all.find((e) => e.tone === "created");
  const submitIdx = all.map((e) => e.tone === "sent" && !e.process).lastIndexOf(true);
  const submitted = submitIdx >= 0 ? all[submitIdx] : null;

  const out = [{ key: "created", title: "MBMR created", status: created ? "finish" : "wait", tone: "created",
                 desc: created ? `${dayOf(created.at)} · ${by(created) || "—"}` : "" }];

  const procRows = [];
  processes.forEach((name) => {
    const evs = all.filter((e) => e.process === name);
    const sentIdx = evs.map((e) => e.tone === "sent").lastIndexOf(true);
    const since = sentIdx >= 0 ? evs.slice(sentIdx + 1) : [];
    const approvedProc = since.find((e) => e.tone === "approved" && !e.operation);
    const lastNo = [...since].reverse().find((e) => e.tone === "rejected");
    const opsOk = since.filter((e) => e.tone === "approved" && e.operation).length;
    let st;
    if (approvedProc) st = { status: "finish", tone: "approved", desc: `Approved · ${dayOf(approvedProc.at)}${by(approvedProc) ? ` · ${by(approvedProc)}` : ""}` };
    else if (lastNo) st = { status: "error", tone: "rejected", desc: `Not approved · ${dayOf(lastNo.at)}${by(lastNo) ? ` · ${by(lastNo)}` : ""}${lastNo.comment ? ` — “${lastNo.comment}”` : ""} · fix and re-send` };
    else if (sentIdx >= 0) st = { status: "process", tone: "sent", desc: `In review since ${dayOf(evs[sentIdx].at)}${opsOk ? ` · ${opsOk} operation${opsOk === 1 ? "" : "s"} approved` : ""}${evs[sentIdx].detail ? ` · ${evs[sentIdx].detail}` : ""}` };
    else {
      const added = evs.find((e) => e.tone === "added");
      // A process with no review of its own goes through with the record's approval.
      // Added and not under a review of its own: configured — nothing on it is waiting.
      st = { status: "finish", tone: "added", desc: `Added${added ? ` · ${dayOf(added.at)}` : ""}${added?.actor ? ` · ${added.actor}` : ""}` };
    }
    procRows.push({ key: `p-${name}`, title: name, process: true, child: true, ...st });
  });

  // "Process configuration" — one stage while the record has no process; once it has, the stage
  // opens into one row per process, each with its own state, and summarises them.
  const anyBad = procRows.some((x) => x.status === "error");
  const anyLive = procRows.some((x) => x.status === "process");
  const allDone = procRows.length > 0 && procRows.every((x) => x.status === "finish");
  const reviewed = procRows.filter((x) => x.tone === "approved").length;
  out.push({
    key: "process-config", title: "Process configuration", tone: "added",
    status: !procRows.length ? (submitted ? "finish" : "process") : anyBad ? "error" : anyLive ? "process" : allDone ? "finish" : "process",
    desc: !procRows.length ? "No process yet — add the first process to the record"
      : `${procRows.length} process${procRows.length === 1 ? "" : "es"}${reviewed ? ` · ${reviewed} approved` : ""}${anyBad ? " · one needs action" : anyLive ? " · in review" : ""}`,
  });
  out.push(...procRows);

  // The flow ends at Finalise: finalised (the MBMR page's Recipe_Status) or submitted from the
  // designer — whichever happened first. The record's own approval chain after it is history,
  // listed under Activity, not a stage here.
  const finalised = all.find((e) => e.title === "Recipe finalised") || submitted;
  const processBlocking = procRows.length === 0 || out.some((x) => x.child && (x.status === "process" || x.status === "error"));
  out.push(finalised
    ? { key: "finalise", title: "Finalised", status: "finish", tone: "final", desc: `${dayOf(finalised.at)} · ${by(finalised) || "—"}` }
    : { key: "finalise", title: "Finalise", status: processBlocking ? "wait" : "process", tone: "final",
        desc: !procRows.length ? "After processes are configured" : processBlocking ? "Waits for the processes above" : "Ready to finalise" });

  // The current stage is the first child that needs something, else the first stage that does.
  let cur = out.findIndex((x) => x.child && (x.status === "process" || x.status === "error"));
  if (cur < 0) cur = out.findIndex((x) => !x.child && (x.status === "process" || x.status === "error"));
  return { list: out, current: cur };
}

/** Where the record stands, read from what happened last to the record itself. */
function standing(all) {
  const own = all.filter((e) => !e.process && e.tone !== "created");
  const lastEv = own[own.length - 1];
  if (!lastEv) return { label: "Draft", tone: "neutral" };
  if (lastEv.tone === "final") return { label: "Approved", tone: "approved" };
  if (lastEv.tone === "rejected") return { label: "Rejected", tone: "rejected" };
  if (lastEv.tone === "returned") return { label: "Returned for correction", tone: "returned" };
  if (lastEv.tone === "sent" || lastEv.tone === "approved") return { label: "In review", tone: "sent" };
  return { label: "Draft", tone: "neutral" };
}

/** The tracker + activity for one record's history payload (`getRecordHistory`). Used by the
 *  card's History drawer and by the MBMR PR page, one revision at a time. */
export function RecordHistoryBody({ data, showSummary = true }) {
  const { isDark } = useTheme();
  const [scope, setScope] = useState("all");       // all · record · process
  const [process, setProcess] = useState(null);
  useEffect(() => { setScope("all"); setProcess(null); }, [data]);
  const all = data?.events || [];
  const shown = useMemo(() => {
    let list = all;
    if (scope === "record") list = all.filter((e) => !e.process);
    if (scope === "process") list = all.filter((e) => e.process && (!process || e.process === process));
    return foldAdded(list);
  }, [all, scope, process]);
  const days = useMemo(() => {
    const groups = [];
    shown.forEach((e) => {
      const d = dayOf(e.at);
      if (!groups.length || groups[groups.length - 1].day !== d) groups.push({ day: d, items: [] });
      groups[groups.length - 1].items.push(e);
    });
    return groups;
  }, [shown]);
  const track = useMemo(() => stages(data), [data]);
  const stand = useMemo(() => standing(all), [all]);

  const ink = isDark ? "#e5e7eb" : "#111827";
  const sub = isDark ? "#9ca3af" : "#6b7280";
  const card = isDark ? "#1f2937" : "#ffffff";
  const line = isDark ? "#374151" : "#e5e7eb";
  const panel = isDark ? "#111827" : "#f8fafc";
  const S = TONE[stand.tone] || TONE.neutral;

  if (!data) return null;
  return (
        <div>
          {/* ── summary ───────────────────────────────────────────────────────── */}
          <div style={{ padding: "16px 24px", background: card, borderBottom: `1px solid ${line}` }}>
            {showSummary ? <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 14 }}>
              {data.recipe.code ? <Tag style={{ fontFamily: "monospace", margin: 0 }}>{data.recipe.code}</Tag> : null}
              <Tag style={{ margin: 0 }}>v{data.recipe.version}</Tag>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 10px", borderRadius: 999,
                             background: S.bg, color: S.color, fontSize: 13.5, fontWeight: 600 }}>
                <S.Icon size={13} /> {stand.label}
              </span>
              <span style={{ marginLeft: "auto", fontSize: 13.5, color: sub }}>
                {(() => { const np = (data.recipe.processes || []).length, na = all.filter((e) => e.tone === "approved").length;
                  return `${np} process${np === 1 ? "" : "es"} · ${na} approval${na === 1 ? "" : "s"}`; })()}
                {all.some((e) => e.tone === "rejected") ? ` · ${all.filter((e) => e.tone === "rejected").length} not approved` : ""}
              </span>
            </div> : null}
            {/* where it stands — fixed ends, one stage per process between them */}
            <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: sub, marginBottom: 8 }}>Where it stands</div>
            <div>
              {track.list.map((st, i) => {
                const t = TONE[st.tone] || TONE.neutral;
                const isCur = i === track.current;
                const done = st.status === "finish", bad = st.status === "error", live = st.status === "process";
                const ring = done ? t.color : bad ? TONE.rejected.color : live ? t.color : line;
                const Icon = done ? CheckCircle2 : bad ? XCircle : t.Icon;
                const next = track.list[i + 1];
                return (
                  <div key={st.key} style={{ display: "flex", gap: 12, marginLeft: st.child ? 34 : 0 }} data-testid="stage">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 26, flexShrink: 0 }}>
                      <span style={{ width: 26, height: 26, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                                     background: done ? ring : bad ? TONE.rejected.bg : live ? t.bg : "transparent",
                                     border: `2px solid ${ring}`, color: done ? "#fff" : ring,
                                     boxShadow: isCur ? `0 0 0 4px ${bad ? TONE.rejected.bg : t.bg}` : "none" }}>
                        {st.status === "wait" ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: line }} /> : <Icon size={13} />}
                      </span>
                      {next ? <span style={{ flex: 1, width: 2, minHeight: 14, background: done ? ring : line }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, paddingBottom: next ? 10 : 0,
                                  ...(st.process ? { borderLeft: "none" } : {}) }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: isCur ? 700 : 600, fontSize: 14.5, color: st.status === "wait" ? sub : ink }}>{st.title}</span>
                        {isCur ? <Tag color={bad ? "error" : "processing"} style={{ margin: 0, fontSize: 12.5 }}>{bad ? "Needs action" : "Current stage"}</Tag> : null}
                      </div>
                      {st.desc ? <div style={{ fontSize: 13.5, color: bad ? TONE.rejected.color : sub, marginTop: 2 }}>{st.desc}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── filter ────────────────────────────────────────────────────────── */}
          <div style={{ padding: "12px 24px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
                        background: panel, borderBottom: `1px solid ${line}`, position: "sticky", top: 0, zIndex: 2 }}>
            <Segmented size="small" value={scope} onChange={setScope}
                       options={[{ label: "Everything", value: "all" }, { label: "Record", value: "record" }, { label: "Processes", value: "process" }]} />
            {scope === "process" ? (
              <Select size="small" allowClear placeholder="All processes" style={{ minWidth: 220 }} value={process}
                      onChange={setProcess} options={(data.recipe.processes || []).map((p) => ({ value: p, label: p }))} />
            ) : null}
            <span style={{ marginLeft: "auto", fontSize: 13.5, color: sub }}>{shown.length} event{shown.length === 1 ? "" : "s"}</span>
          </div>

          {/* ── the line, one group per day ──────────────────────────────────── */}
          <div style={{ padding: "8px 24px 32px" }}>
            {days.length === 0 ? <div style={{ padding: 32 }}><Empty description="Nothing has happened here yet." /></div> : days.map((g) => (
              <div key={g.day}>
                <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: sub, margin: "16px 0 8px" }}>{g.day}</div>
                {g.items.map((e, i) => {
                  const t = TONE[e.tone] || TONE.neutral;
                  const lastInDay = i === g.items.length - 1;
                  return (
                    <div key={`${e.at}-${i}`} style={{ display: "flex", gap: 12 }} data-testid="history-event">
                      {/* rail */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 30, flexShrink: 0 }}>
                        <span style={{ width: 30, height: 30, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                                       background: t.bg, color: t.color }}>
                          <t.Icon size={15} />
                        </span>
                        {!lastInDay ? <span style={{ flex: 1, width: 2, background: line, minHeight: 10 }} /> : null}
                      </div>
                      {/* card */}
                      <div style={{ flex: 1, minWidth: 0, background: card, border: `1px solid ${line}`, borderLeft: `3px solid ${t.color}`,
                                    borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                          <span style={{ fontWeight: 600, color: ink, fontSize: 15 }}>{e.title}</span>
                          <span style={{ marginLeft: "auto", fontSize: 13.5, color: sub, whiteSpace: "nowrap" }}>{timeOf(e.at)}</span>
                        </div>
                        {e.processes && e.processes.length > 1 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                            {e.processes.map((p) => <Tag key={p} style={{ margin: 0 }}>{p}</Tag>)}
                          </div>
                        ) : e.process ? (
                          <div style={{ marginTop: 4 }}><Tag color="default" style={{ margin: 0 }}>{e.process}</Tag></div>
                        ) : null}
                        {e.operation ? <div style={{ fontSize: 13.5, color: sub, marginTop: 4 }}>Operation · {e.operation}</div> : null}
                        {e.detail ? <div style={{ fontSize: 13.5, color: sub, marginTop: 4 }}>{e.detail}</div> : null}
                        {(e.actor || e.role) ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                            {e.actor ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, color: ink }}>
                                <span style={{ width: 20, height: 20, borderRadius: "50%", background: t.bg, color: t.color, fontSize: 12.5, fontWeight: 700,
                                               display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                  {e.actor.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                {e.actor}
                              </span>
                            ) : null}
                            {e.role ? <Tag bordered={false} style={{ margin: 0, fontSize: 12.5 }}>{e.role}</Tag> : null}
                          </div>
                        ) : null}
                        {e.comment ? (
                          <Tooltip title={e.comment.length > 140 ? e.comment : null}>
                            <div style={{ marginTop: 8, padding: "6px 10px", background: t.bg, borderRadius: 6, fontSize: 13.5, color: ink,
                                          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>
                              “{e.comment}”
                            </div>
                          </Tooltip>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
  );
}

export default function RecordHistoryDrawer({ recipe, open, onClose }) {
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !recipe?.id) return;
    let alive = true;
    setLoading(true); setError(null);
    getRecordHistory(recipe.id)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.response?.data?.message || "Could not load the history."); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, recipe?.id]);

  const ink = isDark ? "#e5e7eb" : "#111827";
  const sub = isDark ? "#9ca3af" : "#6b7280";
  const line = isDark ? "#374151" : "#e5e7eb";
  const panel = isDark ? "#111827" : "#f8fafc";

  return (
    <Drawer open={open} onClose={onClose} width={600} destroyOnClose
            styles={{ body: { padding: 0, background: panel }, header: { borderBottom: `1px solid ${line}` } }}
            title={(
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 34, height: 34, borderRadius: 10, display: "inline-flex", alignItems: "center", justifyContent: "center",
                               background: "rgba(22,119,255,0.10)", color: "#1677ff", flexShrink: 0 }}>
                  <History size={18} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 600, color: ink }}>Record history</div>
                  <div style={{ fontSize: 13.5, color: sub, fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {recipe?.name || data?.recipe?.name}
                  </div>
                </div>
              </div>
            )}>
      {loading ? <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 10 }} /></div> : error ? (
        <div style={{ padding: 40 }}><Empty description={error} /></div>
      ) : !data ? null : (
        <RecordHistoryBody data={data} />
      )}
    </Drawer>
  );
}
